import { useState, useRef, useEffect } from "react";
import NextLink from "next/link";
import { useRouter } from "next/compat/router";
import {
  Header,
  Avatar,
  IconFa,
  Button,
  Link,
} from "@cruk/cruk-react-components";

import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";
import { isBrowser } from "@fwa/src/utils/browserUtils";
import { imagePathFromImageType } from "@fwa/src/services/imageService";
import { useKey } from "@fwa/src/hooks/useKey";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import { logIn, logOut } from "@fwa/src/services/auth";

import styles from "./styles.module.css";

const ENV_NAME = process.env["NEXT_PUBLIC_ENV_NAME"] || "";

export const TopSection = () => {
  const { trackEventGtm, trackError } = useTracking();
  const [brandName] = useBrandContext();

  const navRef = useRef<HTMLElement>(null);
  const buttonRef = useRef<HTMLElement>(null);
  const router = useRouter();
  const [userNavOpen, setUserNavOpen] = useState(false);
  const [fundraiserState, setFundraiserState] = useFundraiserContext();
  const { fundraiser } = fundraiserState;
  const hasWindow = isBrowser;
  const loginButtonDisabled = fundraiserState?.loggedInStatus !== "loggedOut";

  const isAdmin = /@cancer.org.uk$/.test(fundraiser?.emailAddress || "");
  const adminUrl =
    ENV_NAME === "production"
      ? "https://admin.fundraise.cancerresearchuk.org/admin"
      : `https://integration.admin.ofr.int.cruk.org/`;

  const avatartUrl = fundraiser?.profileImage
    ? fundraiser.profileImage.entityType === "ImageFacebook"
      ? fundraiser.profileImage.url
      : imagePathFromImageType(fundraiser.profileImage)
    : "";

  const handleAvatarClicked = () => {
    setUserNavOpen(!userNavOpen);
  };

  const handleLogOutClicked = () => {
    setUserNavOpen(false);
    trackEventGtm({
      event: "logout",
      loggedInUserId: null,
    });
    if (
      router?.pathname.startsWith("/fundraiser/") ||
      router?.pathname.startsWith("/signup/") ||
      router?.pathname.startsWith("/createpage/")
    ) {
      router?.push("/").catch((err) => {
        trackError(err as Error, { component: "TopSection" });
      });
    }
    logOut()
      .then(() => {
        setFundraiserState({
          ...fundraiserState,
          loggedInStatus: "loggedOut",
          fundraiser: null,
        });
        return undefined;
      })
      .catch((err) => {
        trackError(err as Error, { component: "TopSection" });
      });
  };

  const handleLinkClicked = () => {
    setUserNavOpen(false);
  };

  // outside click closes popover
  const clickShouldCloseMenu = (e: MouseEvent) => {
    const eventTarget = e.target as HTMLElement;
    // ignore button click let button clickhander work
    const isDescendantButton = buttonRef?.current?.contains(eventTarget);
    if (isDescendantButton) {
      return;
    }
    const isDescendantOfNav = navRef?.current?.contains(eventTarget);
    if (!isDescendantOfNav) {
      setUserNavOpen(false);
    }
  };

  // escape key closes popover
  const escShouldCloseMenu = () => {
    const focusedElement = document.activeElement;
    const isDescendantButton = buttonRef?.current?.contains(focusedElement);
    const isDescendantOfNav = navRef?.current?.contains(focusedElement);
    if (isDescendantButton || isDescendantOfNav) {
      setUserNavOpen(false);
      if (buttonRef?.current) buttonRef.current.focus();
    }
  };

  useKey(
    () => {
      escShouldCloseMenu();
    },
    {
      detectKeys: ["Escape"],
    },
    [],
  );

  useEffect(() => {
    if (hasWindow) {
      document.addEventListener("click", clickShouldCloseMenu, true);
    }
    return () => {
      if (hasWindow) {
        document.removeEventListener("click", clickShouldCloseMenu, true);
      }
    };
  }, [hasWindow]);

  return (
    <div className={styles["top-section-wrapper"]} data-component="top-section">
      <Header
        themeName={brandName || "cruk"}
        siteSlogan={
          brandName === "cruk" ? "Together we are beating cancer" : undefined
        }
        suppressHydrationWarning
        isSticky={true}
      >
        <div className={styles["button-wrapper"]}>
          {fundraiser ? (
            <Button
              className={[styles["avatar-button"], "component-button"].join(
                " ",
              )}
              aria-expanded={userNavOpen}
              aria-controls="account-menu"
              ref={buttonRef}
              onClick={handleAvatarClicked}
              aria-label={`Account for ${fundraiser.forename} ${
                fundraiser.surname
              } ${userNavOpen ? "close" : "open"} menu`}
              appearance="secondary"
              data-cta-type="open-menu"
            >
              <div className={styles["row-align-center"]}>
                <span className={styles["avatar-wrapper"]}>
                  <Avatar
                    name={`${fundraiser.forename} ${fundraiser.surname}`}
                    url={avatartUrl}
                    size="s"
                    themeName={brandName}
                  />
                </span>
                <span
                  className={styles["flipping-icon"]}
                  data-is-active={userNavOpen}
                >
                  <IconFa faIcon={faChevronDown} />
                </span>
              </div>
            </Button>
          ) : null}
          {!fundraiser ? (
            <Button
              className={[styles["log-in-button"], "component-button"].join(
                " ",
              )}
              disabled={loginButtonDisabled}
              onClick={logIn}
              appearance="secondary"
            >
              Log in
            </Button>
          ) : null}
        </div>
      </Header>
      {fundraiser ? (
        <nav
          className={styles["user-nav"]}
          id="account-menu"
          ref={navRef}
          data-is-open={userNavOpen}
        >
          <ul>
            <li>
              <NextLink
                href="/fundraiser/dashboard"
                onClick={handleLinkClicked}
                data-cta-type="link-dashboard"
              >
                <Link as="span" textAlign="center">
                  Your dashboard
                </Link>
              </NextLink>
            </li>
            <li>
              <NextLink
                href="/fundraiser/settings"
                onClick={handleLinkClicked}
                data-cta-type="link-account-settings"
              >
                <Link as="span" textAlign="center">
                  Account settings
                </Link>
              </NextLink>
            </li>
            {isAdmin && (
              <li>
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={adminUrl}
                  data-cta-type="link-admin"
                  textAlign="center"
                >
                  Admin
                </Link>
              </li>
            )}
            <li>
              <Link
                textAlign="center"
                href="https://www.cancerresearchuk.org/get-involved/do-your-own-fundraising/practical-fundraising-help/cruk-giving-page-help"
                data-cta-type="link-of-help-page"
              >
                Help
              </Link>
            </li>
            <li>
              <button
                type="button"
                className={["component-link", "spacing-props"].join(" ")}
                data-padding-vertical="xxs"
                onClick={handleLogOutClicked}
                data-cta-type="log-out"
              >
                Log out
              </button>
            </li>
          </ul>
        </nav>
      ) : null}
    </div>
  );
};

export default TopSection;
